// Session Management API - Connected to Backend via API Gateway
// Comprehensive session management and monitoring

import { authenticatedFetch } from './authenticatedFetch';

// Session types
export interface UserSession {
  id: string;
  user_id: string;
  ip_address: string;
  user_agent: string;
  location?: {
    country?: string;
    city?: string;
    region?: string;
  };
  device_info?: {
    browser?: string;
    os?: string;
    device_type?: 'desktop' | 'mobile' | 'tablet';
  };
  is_current: boolean;
  created_at: string;
  last_activity: string;
  expires_at: string;
  is_active: boolean;
}

export interface SessionSummary {
  total_sessions: number;
  active_sessions: number;
  current_session_id: string;
  session_locations: {
    country: string;
    count: number;
  }[];
  device_breakdown: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
  recent_activity: {
    session_id: string;
    ip_address: string;
    last_activity: string;
    location?: string;
  }[];
}

export interface SessionActivity {
  session_id: string;
  user_id: string;
  activity_type: 'login' | 'logout' | 'api_call' | 'page_view' | 'action';
  activity_details: {
    action?: string;
    endpoint?: string;
    page?: string;
    method?: string;
    status_code?: number;
    response_time?: number;
  };
  ip_address: string;
  user_agent: string;
  timestamp: string;
}

// Wrapper function using shared authenticated fetch
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'Session API' });
}

// Session Management API
export const sessionAPI = {
  /**
   * Get all active sessions for current user
   */
  async getUserSessions(): Promise<UserSession[]> {
    return await apiRequest<UserSession[]>('/auth/sessions', {
      method: 'GET',
    });
  },

  /**
   * Get session summary with statistics
   */
  async getSessionSummary(): Promise<SessionSummary> {
    return await apiRequest<SessionSummary>('/auth/sessions/summary', {
      method: 'GET',
    });
  },

  /**
   * Get detailed session information
   */
  async getSessionDetails(sessionId: string): Promise<UserSession> {
    return await apiRequest<UserSession>(`/auth/sessions/${sessionId}`, {
      method: 'GET',
    });
  },

  /**
   * Get session activity log
   */
  async getSessionActivity(sessionId: string, params?: {
    limit?: number;
    offset?: number;
    activity_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<SessionActivity[]> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/auth/sessions/${sessionId}/activity?${queryString}` : `/auth/sessions/${sessionId}/activity`;
    
    return await apiRequest<SessionActivity[]>(url, {
      method: 'GET',
    });
  },

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId: string): Promise<void> {
    return await apiRequest<void>(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Revoke all other sessions (keep current session)
   */
  async revokeAllOtherSessions(): Promise<void> {
    return await apiRequest<void>('/auth/sessions', {
      method: 'DELETE',
    });
  },

  /**
   * Get current session information
   */
  async getCurrentSession(): Promise<UserSession> {
    return await apiRequest<UserSession>('/auth/sessions/current', {
      method: 'GET',
    });
  },

  /**
   * Update current session (extend or refresh)
   */
  async refreshCurrentSession(): Promise<UserSession> {
    return await apiRequest<UserSession>('/auth/sessions/current/refresh', {
      method: 'POST',
    });
  },

  /**
   * Get session security events
   */
  async getSessionSecurityEvents(params?: {
    limit?: number;
    offset?: number;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    event_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/auth/sessions/security-events?${queryString}` : `/auth/sessions/security-events`;
    
    return await apiRequest<any[]>(url, {
      method: 'GET',
    });
  },

  /**
   * Helper method to format session location
   */
  formatSessionLocation(session: UserSession): string {
    if (!session.location) return 'Unknown Location';
    
    const parts = [];
    if (session.location.city) parts.push(session.location.city);
    if (session.location.region) parts.push(session.location.region);
    if (session.location.country) parts.push(session.location.country);
    
    return parts.length > 0 ? parts.join(', ') : 'Unknown Location';
  },

  /**
   * Helper method to format device info
   */
  formatDeviceInfo(session: UserSession): string {
    if (!session.device_info) return 'Unknown Device';
    
    const parts = [];
    if (session.device_info.browser) parts.push(session.device_info.browser);
    if (session.device_info.os) parts.push(session.device_info.os);
    if (session.device_info.device_type) parts.push(session.device_info.device_type);
    
    return parts.length > 0 ? parts.join(' on ') : 'Unknown Device';
  },

  /**
   * Helper method to determine if session is suspicious
   */
  isSuspiciousSession(session: UserSession, userSessions: UserSession[]): boolean {
    // Check for unusual location
    const userLocations = userSessions
      .filter(s => s.location?.country)
      .map(s => s.location!.country!);
    
    const uniqueLocations = [...new Set(userLocations)];
    
    // If this is a new country and user has been active in other countries
    if (uniqueLocations.length > 1 && session.location?.country && 
        !userLocations.includes(session.location.country)) {
      return true;
    }
    
    // Check for unusual device
    const userDevices = userSessions
      .filter(s => s.device_info?.device_type)
      .map(s => s.device_info!.device_type!);
    
    const uniqueDevices = [...new Set(userDevices)];
    
    // If this is a new device type and user typically uses different devices
    if (uniqueDevices.length > 1 && session.device_info?.device_type && 
        !userDevices.includes(session.device_info.device_type)) {
      return true;
    }
    
    return false;
  },

  /**
   * Helper method to get session duration
   */
  getSessionDuration(session: UserSession): string {
    const createdAt = new Date(session.created_at);
    const lastActivity = new Date(session.last_activity);
    
    const durationMs = lastActivity.getTime() - createdAt.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    if (durationMinutes < 60) {
      return `${durationMinutes} minutes`;
    } else if (durationMinutes < 24 * 60) {
      const hours = Math.floor(durationMinutes / 60);
      return `${hours} hours`;
    } else {
      const days = Math.floor(durationMinutes / (24 * 60));
      return `${days} days`;
    }
  },

  /**
   * Helper method to check if session is about to expire
   */
  isSessionExpiringSoon(session: UserSession, warningMinutes: number = 15): boolean {
    const expiresAt = new Date(session.expires_at);
    const now = new Date();
    const timeUntilExpiry = expiresAt.getTime() - now.getTime();
    const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));
    
    return minutesUntilExpiry <= warningMinutes && minutesUntilExpiry > 0;
  },
}; 